/**
 * Voice WebRTC DataChannel lifecycle. Parallel to `connection.ts` (the
 * WebSocket transport) but uses an unreliable, unordered RTCDataChannel
 * so we get UDP-like loss / latency characteristics instead of TCP's
 * head-of-line blocking.
 *
 * Two phases:
 *
 *   1. **Signalling.** Opens a WebSocket to /api/voice-rtc/signal with
 *      the same query-param auth as the legacy voice WS. Bridge sends a
 *      'hello' message with the allocated session id, then the browser
 *      generates an SDP offer and ICE candidates and exchanges them
 *      with the bridge over this WS. Stays open for the life of the
 *      voice session for ICE renegotiation / clean teardown.
 *
 *   2. **Voice DataChannel.** Once the peer connection negotiates, the
 *      browser-created "voice" DataChannel carries the same binary
 *      AudioUp / AudioFwd frames the legacy WS voice path used (defined
 *      in `frames.ts`). No protocol change at the wire-format layer;
 *      only the transport changes.
 *
 * If WebRTC negotiation hasn't completed within
 * RTC_NEGOTIATION_TIMEOUT_MS, the connection self-aborts. Callers can
 * then fall back to the legacy WS path - the public interface here
 * mirrors `VoiceConnection` exactly (`start`, `stop`, `sendOpus`,
 * `reconnectNow`, `onState`, `onAuthFailed`).
 */

import { refreshSession } from '../api.ts'
import { baseWsOrigin, getRefreshToken } from '../auth.ts'
import { AudioEngine } from './audio.ts'
import { decodeDownlink, encodeAudioUp, encodeHeartbeat } from './frames.ts'

const HEARTBEAT_INTERVAL_MS = 5_000
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000

/**
 * Cap on how long we'll wait for the DataChannel to open after starting
 * negotiation. If it doesn't open by then, something is wrong (ICE
 * failed, the bridge isn't responding, the user is behind a strict
 * firewall) and we should error out so the caller can fall back to the
 * legacy WS path.
 */
const RTC_NEGOTIATION_TIMEOUT_MS = 8_000

const DEFAULT_ICE_SERVERS: ReadonlyArray<RTCIceServer> = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
]

export interface VoiceRtcConnectionOptions {
  readonly serverId: string
  /** Stable per-page client id so the bridge treats our own reconnects as a
   * replace (not a 4006 rejection). Shared with the WS fallback path. */
  readonly clientInstanceId: string
  readonly engine: AudioEngine
  readonly onState: (state: ConnectionState) => void
  /**
   * Called when the bridge rejects our session token irrecoverably
   * (close code 4002). Caller wipes refresh token and routes back to link.
   */
  readonly onAuthFailed: () => void
  /**
   * Called when negotiation times out OR ICE permanently fails. The page
   * can use this to fall back to the legacy WS voice path.
   */
  readonly onRtcUnavailable?: (reason: string) => void
}

export type ConnectionState =
  | { kind: 'connecting' }
  | { kind: 'connected'; sinceMs: number }
  | { kind: 'reconnecting'; attempt: number; nextDelayMs: number; reason: string }
  | { kind: 'stopped' }

type SignalMessage =
  | { type: 'hello'; sessionId: number; agid: string; linkId: string }
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | { type: 'ice'; candidate: RTCIceCandidateInit | null }
  | { type: 'error'; code: string; message: string }

export class VoiceRtcConnection {
  private readonly opts: VoiceRtcConnectionOptions
  private signalWs: WebSocket | null = null
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private negotiationTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempt = 0
  private uplinkSeq = 0
  private stopped = false

  constructor(opts: VoiceRtcConnectionOptions) {
    this.opts = opts
  }

  async start(): Promise<void> {
    this.stopped = false
    await this.connectOnce()
  }

  stop(): void {
    this.stopped = true
    this.cleanupTimers()
    this.cleanupPeer()
    this.opts.onState({ kind: 'stopped' })
  }

  /**
   * Force an immediate reconnect attempt, bypassing pending backoff.
   * Used by the wake handler after page-foreground returns. Idempotent
   * (no-op if already connected).
   */
  reconnectNow(): void {
    if (this.stopped) return
    if (this.dc !== null && this.dc.readyState === 'open') return
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempt = 0
    void this.connectOnce()
  }

  sendOpus(opus: Uint8Array): void {
    const dc = this.dc
    if (dc === null || dc.readyState !== 'open') return
    this.uplinkSeq = (this.uplinkSeq + 1) >>> 0
    try {
      dc.send(encodeAudioUp(this.uplinkSeq, opus))
    } catch {
      // DataChannel was torn down between the state check and send. The
      // 'close' handler will run and trigger reconnect.
    }
  }

  // ---- Internal state machine -------------------------------------------

  private async connectOnce(): Promise<void> {
    this.opts.onState({ kind: 'connecting' })

    const refreshToken = getRefreshToken()
    if (refreshToken === null) {
      this.opts.onAuthFailed()
      return
    }

    let sessionToken: string
    try {
      const { sessionToken: t } = await refreshSession(refreshToken)
      sessionToken = t
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('invalid_token') || msg.includes('http_401')) {
        this.opts.onAuthFailed()
        return
      }
      this.scheduleReconnect(`refresh failed: ${msg}`)
      return
    }

    const signalUrl =
      `${baseWsOrigin()}/api/voice-rtc/signal` +
      `?token=${encodeURIComponent(sessionToken)}` +
      `&serverId=${encodeURIComponent(this.opts.serverId)}` +
      `&client=${encodeURIComponent(this.opts.clientInstanceId)}`

    const ws = new WebSocket(signalUrl)
    this.signalWs = ws

    let helloReceived = false
    let pc: RTCPeerConnection | null = null
    let dc: RTCDataChannel | null = null

    // Hard cutoff so a half-open negotiation doesn't pin the UI in
    // 'connecting' forever. If we hit this, fall back via
    // onRtcUnavailable so the page can try the legacy WS path.
    this.negotiationTimer = setTimeout(() => {
      if (this.dc !== null && this.dc.readyState === 'open') return
      const reason = `rtc negotiation timed out after ${RTC_NEGOTIATION_TIMEOUT_MS} ms`
      this.opts.onRtcUnavailable?.(reason)
      this.cleanupPeer()
      this.scheduleReconnect(reason)
    }, RTC_NEGOTIATION_TIMEOUT_MS)

    ws.addEventListener('open', () => {
      // Don't transition to 'connected' here - the DataChannel still
      // needs to open. Stay in 'connecting' until dc.onopen fires.
      try {
        pc = new RTCPeerConnection({ iceServers: [...DEFAULT_ICE_SERVERS] })
      } catch (err) {
        this.scheduleReconnect(`RTCPeerConnection construction failed: ${describeError(err)}`)
        return
      }
      this.pc = pc

      // Trickle local ICE candidates to the bridge.
      pc.onicecandidate = (event): void => {
        if (ws.readyState !== WebSocket.OPEN) return
        const candidate = event.candidate
        // null signals end of gathering. Pass it through so the bridge
        // can stop waiting.
        ws.send(JSON.stringify({
          type: 'ice',
          candidate: candidate !== null ? candidate.toJSON() : null,
        } satisfies SignalMessage))
      }

      pc.oniceconnectionstatechange = (): void => {
        if (pc === null) return
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          // Don't tear down on 'disconnected' - it can be transient.
          // 'failed' is terminal; reconnect.
          if (pc.iceConnectionState === 'failed') {
            this.scheduleReconnect(`ice failed (state=${pc.iceConnectionState})`)
          }
        }
      }

      try {
        dc = pc.createDataChannel('voice', { ordered: false, maxRetransmits: 0 })
        dc.binaryType = 'arraybuffer'
      } catch (err) {
        this.scheduleReconnect(`createDataChannel failed: ${describeError(err)}`)
        return
      }
      this.dc = dc

      dc.addEventListener('open', () => {
        if (this.negotiationTimer !== null) {
          clearTimeout(this.negotiationTimer)
          this.negotiationTimer = null
        }
        this.reconnectAttempt = 0
        this.opts.onState({ kind: 'connected', sinceMs: Date.now() })
        this.heartbeatTimer = setInterval(() => {
          if (dc !== null && dc.readyState === 'open') {
            try { dc.send(encodeHeartbeat()) } catch { /* close handler will pick up */ }
          }
        }, HEARTBEAT_INTERVAL_MS)
      })

      dc.addEventListener('message', (event: MessageEvent): void => {
        if (!(event.data instanceof ArrayBuffer)) return
        const frame = decodeDownlink(event.data)
        if (frame === null) return
        if (frame.type === 'heartbeat') return
        if (frame.type === 'audioFwd') {
          this.opts.engine.ingestOpus(frame.senderSessionId, frame.agid, frame.opus)
        }
      })

      dc.addEventListener('close', () => {
        if (this.heartbeatTimer !== null) {
          clearInterval(this.heartbeatTimer)
          this.heartbeatTimer = null
        }
        if (this.stopped) return
        this.scheduleReconnect('datachannel closed')
      })

      // Generate the offer and send it via the signalling WS once the
      // local description is set.
      void (async (): Promise<void> => {
        if (pc === null) return
        try {
          const offer = await pc.createOffer()
          await pc.setLocalDescription(offer)
          if (pc.localDescription !== null) {
            ws.send(JSON.stringify({
              type: 'offer',
              sdp: pc.localDescription.sdp,
            } satisfies SignalMessage))
          }
        } catch (err) {
          this.scheduleReconnect(`offer failed: ${describeError(err)}`)
        }
      })()
    })

    ws.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return
      let msg: SignalMessage | null = null
      try {
        msg = JSON.parse(event.data) as SignalMessage
      } catch {
        return
      }
      if (msg === null) return
      void this.handleSignalMessage(msg, () => { helloReceived = true })
    })

    ws.addEventListener('close', (event) => {
      if (this.stopped) return
      // 4002 = invalid token, terminal. Match the legacy WS path's
      // semantics so the page can route back to link.
      if (event.code === 4002) {
        this.opts.onAuthFailed()
        this.cleanupPeer()
        return
      }
      this.cleanupPeer()
      this.scheduleReconnect(
        helloReceived
          ? `signal ws closed (code=${event.code} reason=${event.reason})`
          : `signal ws closed before hello (code=${event.code})`,
      )
    })

    ws.addEventListener('error', () => {
      // 'close' will follow and own the reconnect logic.
    })
  }

  private async handleSignalMessage(
    msg: SignalMessage,
    markHelloReceived: () => void,
  ): Promise<void> {
    if (msg.type === 'hello') {
      markHelloReceived()
      return
    }
    if (msg.type === 'answer') {
      if (this.pc === null) return
      try {
        await this.pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp })
      } catch (err) {
        this.scheduleReconnect(`setRemoteDescription failed: ${describeError(err)}`)
      }
      return
    }
    if (msg.type === 'ice') {
      if (this.pc === null) return
      if (msg.candidate === null) return // bridge signalled end-of-candidates
      try {
        await this.pc.addIceCandidate(msg.candidate)
      } catch {
        // Race between local description setup and remote candidates; safe
        // to swallow - WebRTC retries internally via the SDP exchange.
      }
      return
    }
    if (msg.type === 'error') {
      this.scheduleReconnect(`signal error: ${msg.code} ${msg.message}`)
    }
  }

  private scheduleReconnect(reason: string): void {
    if (this.stopped) return
    this.cleanupPeer()
    this.reconnectAttempt += 1
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** Math.min(this.reconnectAttempt - 1, 5),
      RECONNECT_MAX_MS,
    )
    this.opts.onState({
      kind: 'reconnecting',
      attempt: this.reconnectAttempt,
      nextDelayMs: delay,
      reason,
    })
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.connectOnce()
    }, delay)
  }

  private cleanupTimers(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.negotiationTimer !== null) {
      clearTimeout(this.negotiationTimer)
      this.negotiationTimer = null
    }
  }

  private cleanupPeer(): void {
    if (this.dc !== null) {
      try { this.dc.close() } catch { /* may already be closing */ }
      this.dc = null
    }
    if (this.pc !== null) {
      try { this.pc.close() } catch { /* may already be closed */ }
      this.pc = null
    }
    if (this.signalWs !== null) {
      try { this.signalWs.close(1000, 'client done') } catch { /* may already be closed */ }
      this.signalWs = null
    }
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.negotiationTimer !== null) {
      clearTimeout(this.negotiationTimer)
      this.negotiationTimer = null
    }
  }
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message
  return String(err)
}
